import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import { authApi } from "../../api/auth";
import { useAuth } from "../../context/AuthContext";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

export default function AccountSecurityPage() {
  const { user } = useAuth();
  const [form, setForm] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.currentPassword) {
      setError("Ingresá tu contraseña actual.");
      return;
    }
    if (form.newPassword.length < 8) {
      setError("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Las contraseñas nuevas no coinciden.");
      return;
    }
    if (form.currentPassword === form.newPassword) {
      setError("La nueva contraseña debe ser diferente de la actual.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setMessage("");
      const data = await authApi.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setMessage(data?.message || "Contraseña cambiada correctamente.");
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      setError(err.message || "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPasswords ? "Ocultar contraseñas" : "Mostrar contraseñas"}
        onClick={() => setShowPasswords((value) => !value)}
        edge="end"
      >
        {showPasswords ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Container maxWidth="md" disableGutters>
      <Stack spacing={3}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box sx={{ width: 46, height: 46, borderRadius: 3, display: "grid", placeItems: "center", color: BRAND.orange, bgcolor: "rgba(243,90,58,.1)" }}>
              <SecurityRoundedIcon />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                Seguridad de la cuenta
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,.62)" }}>
                Cambiá la contraseña de {user?.email || "tu cuenta"}.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,.08)" }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <LockResetRoundedIcon sx={{ color: BRAND.orange }} />
              <Box>
                <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
                  Cambiar contraseña
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(25,36,47,.62)" }}>
                  Al guardarla, las demás sesiones abiertas quedarán cerradas por seguridad.
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 3 }} />

            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.25}>
                <TextField
                  label="Contraseña actual"
                  name="currentPassword"
                  type={showPasswords ? "text" : "password"}
                  value={form.currentPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoComplete="current-password"
                  InputProps={{ endAdornment: passwordAdornment }}
                />
                <TextField
                  label="Nueva contraseña"
                  name="newPassword"
                  type={showPasswords ? "text" : "password"}
                  value={form.newPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoComplete="new-password"
                  inputProps={{ minLength: 8, maxLength: 128 }}
                  helperText="Mínimo 8 caracteres. No puede ser igual a la contraseña actual."
                />
                <TextField
                  label="Repetir nueva contraseña"
                  name="confirmPassword"
                  type={showPasswords ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoComplete="new-password"
                  inputProps={{ minLength: 8, maxLength: 128 }}
                />
                <Button
                  type="submit"
                  disabled={loading}
                  variant="contained"
                  size="large"
                  sx={{ alignSelf: { sm: "flex-start" }, minWidth: 220, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900, py: 1.25 }}
                >
                  {loading ? "Guardando..." : "Cambiar contraseña"}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}
